import { InjectionToken, Signal } from '@angular/core';

/**
 * Abstraction over the user's authenticated session. The same contract Portal defines, so a
 * single implementation can serve every Rock Apps client.
 */
export interface SessionProvider {
  /** True when a usable access token is currently held. */
  readonly isAuthenticated: Signal<boolean>;

  /** The "role" claim values on the current access token (empty when signed out). */
  readonly roles: Signal<string[]>;

  /**
   * Link to the signed-in person's own picture on Identity, from the ID token's standard `picture`
   * claim. Null when they have not set one, or when signed out.
   */
  readonly picture: Signal<string | null>;

  /** The current access token, or null. Does not trigger a refresh. */
  getAccessToken(): string | null;

  /** Returns a valid access token, refreshing first if it is expired or near expiry. */
  getValidAccessToken(): Promise<string | null>;

  /** Begins the authorization-code + PKCE redirect to Identity. */
  login(returnUrl?: string): Promise<void>;

  /** Completes the redirect callback: exchanges the code for tokens, returns the return URL. */
  handleCallback(): Promise<string>;

  /** Clears local tokens and ends the Identity session ("Sign out of Rock Apps"). */
  logout(): void;
}

/** DI token the app binds to a concrete {@link SessionProvider}. */
export const SESSION_PROVIDER = new InjectionToken<SessionProvider>('SESSION_PROVIDER');
